import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { nb } from 'date-fns/locale';
import classes from './DateRangePicker.module.css';
import { DateUtils } from '../../utils/DateUtils';
import {
  DayPicker,
  DateRange as DateRangeReactDayPicker,
  getDefaultClassNames,
  ClassNames,
  TZDate,
} from 'react-day-picker';
import 'react-day-picker/style.css';
import { Paragraph } from '../Paragraph';
import { CalendarIcon, XMarkIcon } from '@navikt/aksel-icons';
import { Heading } from '../Heading';
import { useState } from 'react';
import { Dialog } from '../Dialog';

export type DateRange = {
  fromDate: string;
  toDate: string;
};

type DateRangePickerProps = {
  names: { from: string; to: string };
  values: { from: Date; to: Date };
  setDateRange: React.Dispatch<React.SetStateAction<DateRange>>;
};

type RangeButtonOptions = {
  labelKey: string;
  monthValue: number;
};

export const dateRangePickerTimeZone = 'Europe/Oslo';

export const DateRangePicker = ({
  names,
  values,
  setDateRange,
}: DateRangePickerProps) => {
  const { t } = useTranslation();
  const [toggleDatePicker, setToggleDatePicker] = useState<boolean>(false);
  const [currentRangeButtonActive, setCurrentRangeButtonActive] = useState<
    number | undefined
  >(3);

  const rangeButtonOptions: RangeButtonOptions[] = [
    {
      labelKey: 'dateRangePicker.ranges.lastMonth',
      monthValue: 1,
    },
    {
      labelKey: 'dateRangePicker.ranges.last3Months',
      monthValue: 3,
    },
    {
      labelKey: 'dateRangePicker.ranges.last6Months',
      monthValue: 6,
    },
    {
      labelKey: 'dateRangePicker.ranges.last12Months',
      monthValue: 12,
    },
  ];

  const combinedPeriodContainerClassnames = cn(classes.periodContainer, {
    [classes.periodContainerActive]: toggleDatePicker,
  });

  const onDateFormChange = (from: Date, to: Date): void => {
    const fromDate = new TZDate(from, dateRangePickerTimeZone).toISOString();
    const toDate = new TZDate(to, dateRangePickerTimeZone).toISOString();
    setDateRange({ fromDate, toDate });
  };

  const dayPickerClassnames: Partial<ClassNames> = {
    ...getDefaultClassNames(),
    root: `${getDefaultClassNames().root} ${classes.dayPickerRoot}`,
    caption_label: `${getDefaultClassNames().caption_label} ${classes.dayPickerCaption_label}`,
  };

  return (
    <div className={classes.container}>
      <button
        className={classes.toggleContainer}
        onClick={() => setToggleDatePicker((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setToggleDatePicker((prev) => !prev);
          }
        }}
        tabIndex={0}
        role="button"
        aria-label={t('dateRangePicker.selectPeriod')}
        aria-expanded={toggleDatePicker}
        aria-controls="date-range-picker"
      >
        <label htmlFor={`${names.from}-${names.to}`} className={classes.label}>
          {t('dateRangePicker.period')}
        </label>
        <div className={combinedPeriodContainerClassnames}>
          <Paragraph
            size="md"
            id={`${names.from}-${names.to}`}
            className={classes.period}
          >
            {DateUtils.getShortDateFromDate(values.from, true)} -{' '}
            {DateUtils.getShortDateFromDate(values.to, true)}
          </Paragraph>
          <CalendarIcon
            aria-hidden="true"
            fontSize="1.5rem"
            color={'var(--text-light)'}
          />
        </div>
      </button>
      <Dialog
        isOpen={toggleDatePicker}
        closeDialog={() => setToggleDatePicker(false)}
      >
        <div id="date-range-picker" className={classes.picker}>
          <div className={classes.header}>
            <Heading as="h4" className={classes.pickerTitle}>
              {t('dateRangePicker.selectPeriodTitle')}
            </Heading>
            <button
              type="button"
              className={classes.closeIcon}
              onClick={() => setToggleDatePicker(false)}
              aria-label={t('dateRangePicker.closePicker')}
            >
              <XMarkIcon aria-hidden="true" fontSize="1.5rem" />
            </button>
          </div>
          <div className={classes.borderTop}>
            <div className={classes.rangesContainer}>
              {rangeButtonOptions.map((button, index) => {
                const combinedRangeButtonClassnames = cn(classes.rangeButton, {
                  [classes.rangeButtonActive]:
                    currentRangeButtonActive === index,
                });
                const label = t(button.labelKey);
                return (
                  <button
                    className={combinedRangeButtonClassnames}
                    onClick={() => {
                      onDateFormChange(
                        DateUtils.getMonthFromCurrent(button.monthValue),
                        new Date(),
                      );
                      setCurrentRangeButtonActive(index);
                      setToggleDatePicker(false);
                    }}
                    key={button.labelKey}
                    aria-pressed={currentRangeButtonActive === index}
                    aria-label={`${t('dateRangePicker.showDataFor')} ${label.toLowerCase()}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <DayPicker
              mode="range"
              locale={nb}
              required
              className={classes.dayPicker}
              classNames={dayPickerClassnames}
              selected={{ from: values.from, to: values.to }}
              timeZone={dateRangePickerTimeZone}
              onSelect={({ from, to }: DateRangeReactDayPicker) => {
                if (!from || !to) {
                  return;
                }
                if (from > to) {
                  return;
                }
                onDateFormChange(from, to);

                if (to !== values.to) {
                  setToggleDatePicker(false);
                }
              }}
              endMonth={new Date()}
              disabled={{ after: new Date() }}
              defaultMonth={new Date()}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};
